import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import { FaPaw } from "react-icons/fa";
import api, { fileUrl } from "@/lib/api";
import { HERO_IMAGE, SOCIALS } from "@/lib/site";
import { FadeUp, MaskLines } from "@/components/Reveal";
import Marquee from "@/components/Marquee";

const HERO_STATS = ["100% FLUFF", "TARKOV ENJOYER", "VRCHAT ENJOYER"];

const PERSONAS = [
  {
    name: "Husko",
    tag: "THAT'S ME ONLINE",
    color: "#00F0FF",
    desc: "The one in the suit. Lives for fursuiting, cold drinks with friends, and one more raid before bed.",
    chips: ["Suit pilot", "Tarkov enjoyer", "VRChat now & then"],
  },
  {
    name: "Felix",
    tag: "THE SUIT",
    color: "#FF2A85",
    desc: "The husky himself. Speaks fluent Awoo, demands cuddles, escalates zoomies on sight.",
    chips: ["Fluff reactor", "Cuddles enjoyer", "Zoomies dept."],
  },
];

export default function Home() {
  const heroRef = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 18 });
  const sy = useSpring(my, { stiffness: 60, damping: 18 });
  const imgX = useTransform(sx, [-0.5, 0.5], [-16, 16]);
  const imgY = useTransform(sy, [-0.5, 0.5], [-12, 12]);
  const rotX = useTransform(sy, [-0.5, 0.5], [5, -5]);
  const rotY = useTransform(sx, [-0.5, 0.5], [-7, 7]);
  const orbX = useTransform(sx, [-0.5, 0.5], [36, -36]);
  const orbY = useTransform(sy, [-0.5, 0.5], [24, -24]);

  const [snaps, setSnaps] = useState([]);
  useEffect(() => {
    api
      .get("/gallery")
      .then(({ data }) => setSnaps(data.slice(0, 5)))
      .catch(() => {});
  }, []);

  const handleMouse = (e) => {
    if (!heroRef.current) return;
    const r = heroRef.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <>
      <section
        ref={heroRef}
        onMouseMove={handleMouse}
        className="hero-glow relative flex min-h-screen items-center overflow-hidden pt-16"
      >
        <motion.div
          style={{ x: orbX, y: orbY }}
          className="pointer-events-none absolute -left-24 top-24 h-80 w-80 rounded-full bg-husko-cyan/20 blur-3xl"
        />
        <motion.div
          style={{ x: orbX, y: orbY }}
          className="pointer-events-none absolute -right-20 bottom-16 h-96 w-96 rounded-full bg-husko-magenta/20 blur-3xl"
        />

        <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-12 px-4 py-16 sm:px-6 lg:grid-cols-12 lg:gap-8 lg:px-8">
          <div className="lg:col-span-7">
            <p className="font-mono text-[11px] font-bold uppercase tracking-[0.25em] text-husko-cyan sm:text-xs">
              thehuskobiscuit.com — husko's cozy corner
            </p>
            <h1 className="mt-6 font-display text-4xl font-extrabold uppercase leading-[1.02] tracking-tight text-slate-50 sm:text-5xl lg:text-6xl">
              <MaskLines
                baseDelay={0.15}
                lines={[
                  "The fluffiest",
                  <span key="grad" className="text-gradient-husky">corner of</span>,
                  "the internet",
                ]}
              />
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.65, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="mt-6 max-w-md text-base text-slate-300 sm:text-lg"
            >
              Fursuiter • Gamer • Chief zoomies officer. One
              husky, zero indoor voice. Caution: extremely pettable.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="mt-8 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/gallery"
                data-testid="hero-cta-gallery"
                className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-husko-cyan to-husko-purple px-7 py-3.5 font-display text-sm font-extrabold uppercase tracking-wide text-void transition-transform hover:scale-[1.03] active:scale-95"
              >
                See the gallery
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-cta-contact"
                className="inline-flex items-center gap-2 rounded-full border border-white/15 px-7 py-3.5 font-display text-sm font-extrabold uppercase tracking-wide text-slate-100 transition-colors hover:border-husko-magenta hover:text-husko-magenta"
              >
                Bark at me
                <FaPaw size={14} />
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1, duration: 0.8 }}
              className="mt-12 flex flex-wrap gap-x-8 gap-y-3 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-husko-cyan/90"
            >
              {HERO_STATS.map((s) => (
                <span key={s} className="flex items-center gap-2">
                  <FaPaw size={10} /> {s}
                </span>
              ))}
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="relative flex justify-center lg:col-span-5"
          >
            <motion.div
              style={{ x: imgX, y: imgY, rotateX: rotX, rotateY: rotY, transformPerspective: 900 }}
              className="relative"
            >
              <div className="absolute -inset-1.5 rounded-[2.2rem] bg-gradient-to-br from-husko-cyan via-husko-purple to-husko-magenta opacity-70 blur-md" />
              <img
                src={HERO_IMAGE}
                alt="Husko the husky fursuiter"
                className="relative h-72 w-72 rounded-[2rem] object-cover sm:h-96 sm:w-80 lg:h-[540px] lg:w-[420px]"
                data-testid="hero-image"
              />
              <div className="absolute -left-4 top-10 -rotate-6 rounded-2xl border border-husko-yellow/50 bg-void/85 px-4 py-2 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-husko-yellow backdrop-blur">
                Paw approved
              </div>
              <div className="absolute -right-4 bottom-14 rotate-6 animate-float-slow rounded-2xl border border-husko-cyan/50 bg-void/85 px-4 py-2 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-husko-cyan backdrop-blur">
                100% fluff
              </div>
            </motion.div>
          </motion.div>
        </div>

        <div className="pointer-events-none absolute bottom-6 left-1/2 hidden -translate-x-1/2 items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500 md:flex">
          Scroll for zoomies <ChevronDown size={14} className="animate-bounce" />
        </div>
      </section>

      <Marquee />

      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
        <FadeUp>
          <p className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-husko-cyan">Persona file 001</p>
          <h2 className="mt-3 font-display text-2xl font-extrabold tracking-tight sm:text-3xl lg:text-4xl">
            Two modes. One <span className="text-gradient-husky">fluff.</span>
          </h2>
        </FadeUp>
        <div className="mt-12 grid gap-6 md:grid-cols-2">
          {PERSONAS.map((p, i) => (
            <FadeUp key={p.name} delay={i * 0.1}>
              <motion.div
                whileHover={{ y: -6, rotate: i === 0 ? -0.6 : 0.6 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                className="group relative h-full overflow-hidden rounded-3xl border border-white/10 bg-den p-8 transition-colors lg:p-10"
                data-testid={`home-persona-card-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div
                  className="absolute -right-10 -top-10 h-40 w-40 rounded-full opacity-15 blur-2xl transition-opacity group-hover:opacity-30"
                  style={{ background: p.color }}
                />
                <p className="font-mono text-[11px] font-bold uppercase tracking-[0.25em]" style={{ color: p.color }}>
                  {p.tag}
                </p>
                <h3 className="mt-4 font-display text-3xl font-extrabold uppercase tracking-tight text-slate-50 lg:text-4xl">
                  {p.name}
                </h3>
                <p className="mt-4 max-w-sm leading-relaxed text-slate-300">{p.desc}</p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {p.chips.map((c) => (
                    <span
                      key={c}
                      className="rounded-full border border-white/10 bg-void/60 px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-slate-300"
                    >
                      {c}
                    </span>
                  ))}
                </div>
              </motion.div>
            </FadeUp>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8 lg:pb-32">
        <FadeUp className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-husko-magenta">Fresh drops</p>
            <h2 className="mt-3 font-display text-2xl font-extrabold tracking-tight sm:text-3xl lg:text-4xl">
              Fresh from the den
            </h2>
          </div>
          <Link
            to="/gallery"
            data-testid="home-gallery-open-link"
            className="group inline-flex items-center gap-2 font-display text-sm font-extrabold uppercase tracking-wide text-husko-cyan"
          >
            Open the gallery
            <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
          </Link>
        </FadeUp>

        {snaps.length === 0 ? (
          <FadeUp delay={0.1}>
            <div className="mt-10 flex items-center justify-center rounded-3xl border border-dashed border-white/15 bg-den/50 px-6 py-20 text-center">
              <p className="max-w-sm text-slate-400">
                The den is quiet for now — new pics are dropping soon. Stay fluffy.
              </p>
            </div>
          </FadeUp>
        ) : (
          <div className="mt-10 grid auto-rows-[150px] grid-cols-2 gap-4 sm:auto-rows-[190px] md:grid-cols-4">
            {snaps.map((img, i) => (
              <motion.div
                key={img.id}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, amount: 0.1 }}
                transition={{ duration: 0.5, delay: (i % 4) * 0.07 }}
                className={`group relative overflow-hidden rounded-2xl border border-white/10 bg-den ${
                  i === 0 ? "col-span-2 row-span-2" : ""
                }`}
                data-testid={`home-gallery-teaser-${i}`}
              >
                <img
                  src={fileUrl(img.storage_path)}
                  alt={img.caption || "Husko gallery snap"}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.05]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-void/80 via-transparent to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
              </motion.div>
            ))}
          </div>
        )}
      </section>

      <section className="border-t border-white/5 bg-den/40 py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8">
          <FadeUp>
            <p className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-husko-yellow">Everywhere the husko goes</p>
            <h2 className="mt-3 font-display text-2xl font-extrabold tracking-tight sm:text-3xl lg:text-4xl">
              Find the husko
            </h2>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
              {SOCIALS.map((s) => {
                const Icon = s.icon;
                return (
                  <a
                    key={s.id}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.name}
                    data-testid={`home-social-${s.id}`}
                    className="glass-card rounded-2xl p-4 text-slate-300 transition-all hover:-translate-y-1 hover:scale-105 hover:text-husko-cyan"
                  >
                    <Icon size={26} />
                  </a>
                );
              })}
            </div>
            <p className="mt-8 text-sm text-slate-400">
              Six dens, one husko. Pick your portal on the{" "}
              <Link to="/contact" data-testid="home-social-contact-link" className="font-bold text-husko-cyan underline-offset-4 hover:underline">
                contact page
              </Link>
              .
            </p>
          </FadeUp>
        </div>
      </section>
    </>
  );
}
